import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Plus,
  Wifi,
  WifiOff,
  Settings,
  Trash2,
  MoreVertical,
  Activity,
  Server,
} from 'lucide-react'
import { usePrinterConnectionStore, useAllPrinterStatuses } from '@/stores/printerConnectionStore'
import AddPrinterModal from '@/components/printer/AddPrinterModal'
import PrinterMonitor from '@/components/printer/PrinterMonitor'
import { PROTOCOL_INFO } from '@/lib/printerAdapters'

export default function PrinterManagement() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  
  const printerStatuses = useAllPrinterStatuses()
  const {
    connectPrinter,
    disconnectPrinter,
    removePrinter,
    connectAllPrinters,
    disconnectAllPrinters,
  } = usePrinterConnectionStore()

  // Auto-connect on mount
  useEffect(() => {
    const savedPrinters = usePrinterConnectionStore.getState().savedPrinters
    if (savedPrinters.length > 0) {
      connectAllPrinters()
    }
  }, [])

  const handleRemovePrinter = async (id: string, name: string) => {
    if (confirm(`Are you sure you want to remove "${name}"? This cannot be undone.`)) {
      await removePrinter(id)
    }
  }

  const connectedCount = printerStatuses.filter(
    (p) => p.state?.connectionStatus === 'connected'
  ).length
  const printingCount = printerStatuses.filter(
    (p) => p.state?.printerStatus === 'printing'
  ).length

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Printer Management</h1>
            <p className="text-surface-400 mt-1">
              Connect and monitor your 3D printers in real-time
            </p>
          </div>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Printer
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatsCard
            icon={<Server className="w-5 h-5" />}
            label="Total Printers"
            value={printerStatuses.length}
            color="primary"
          />
          <StatsCard
            icon={<Wifi className="w-5 h-5" />}
            label="Connected"
            value={connectedCount}
            total={printerStatuses.length}
            color="green"
          />
          <StatsCard
            icon={<Activity className="w-5 h-5" />}
            label="Currently Printing"
            value={printingCount}
            color="yellow"
          />
        </div>

        {/* Quick Actions */}
        {printerStatuses.length > 0 && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => connectAllPrinters()}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-800 hover:bg-surface-700 text-surface-300 hover:text-white text-sm transition-colors"
            >
              <Wifi className="w-4 h-4" />
              Connect All
            </button>
            <button
              onClick={() => disconnectAllPrinters()}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-800 hover:bg-surface-700 text-surface-300 hover:text-white text-sm transition-colors"
            >
              <WifiOff className="w-4 h-4" />
              Disconnect All
            </button>
          </div>
        )}

        {/* Printer List */}
        {printerStatuses.length === 0 ? (
          <EmptyState onAddPrinter={() => setIsAddModalOpen(true)} />
        ) : (
          <div className="space-y-4">
            {printerStatuses.map(({ config, state }) => (
              <div key={config.id} className="relative">
                <PrinterMonitor printerId={config.id} />
                
                {/* Dropdown Menu */}
                <div className="absolute top-4 right-12">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setActiveDropdown(activeDropdown === config.id ? null : config.id)
                    }}
                    className="p-1.5 rounded-lg hover:bg-surface-700 text-surface-400 hover:text-white transition-colors"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </button>
                  
                  {activeDropdown === config.id && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="absolute right-0 top-full mt-1 w-48 py-1 rounded-lg bg-surface-800 border border-surface-700 shadow-xl z-10"
                    >
                      {state?.connectionStatus === 'connected' ? (
                        <button
                          onClick={() => {
                            disconnectPrinter(config.id)
                            setActiveDropdown(null)
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-300 hover:bg-surface-700 hover:text-white"
                        >
                          <WifiOff className="w-4 h-4" />
                          Disconnect
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            connectPrinter(config.id)
                            setActiveDropdown(null)
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-300 hover:bg-surface-700 hover:text-white"
                        >
                          <Wifi className="w-4 h-4" />
                          Connect
                        </button>
                      )}
                      <button
                        onClick={() => {
                          // TODO: Open edit modal
                          setActiveDropdown(null)
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-300 hover:bg-surface-700 hover:text-white"
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </button>
                      <hr className="my-1 border-surface-700" />
                      <button
                        onClick={() => {
                          handleRemovePrinter(config.id, config.name)
                          setActiveDropdown(null)
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove Printer
                      </button>
                    </motion.div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Event Log */}
        <EventLog />
      </div>

      {/* Add Printer Modal */}
      <AddPrinterModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />
    </div>
  )
}

function StatsCard({
  icon,
  label,
  value,
  total,
  color,
}: {
  icon: React.ReactNode
  label: string
  value: number
  total?: number
  color: 'primary' | 'green' | 'yellow'
}) {
  const colorClasses = {
    primary: 'bg-primary-500/20 text-primary-400',
    green: 'bg-green-500/20 text-green-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
  }

  return (
    <div className="p-4 rounded-xl bg-surface-900 border border-surface-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>{icon}</div>
        <div>
          <p className="text-sm text-surface-400">{label}</p>
          <p className="text-2xl font-bold text-white">
            {value}
            {total !== undefined && (
              <span className="text-surface-500 text-lg font-normal">/{total}</span>
            )}
          </p>
        </div>
      </div>
    </div>
  )
}

function EmptyState({ onAddPrinter }: { onAddPrinter: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 rounded-xl bg-surface-900 border border-surface-700 border-dashed">
      <div className="w-16 h-16 rounded-full bg-surface-800 flex items-center justify-center mb-4">
        <Server className="w-8 h-8 text-surface-500" />
      </div>
      <h3 className="text-lg font-medium text-white mb-2">No Printers Connected</h3>
      <p className="text-surface-400 text-center max-w-md mb-6">
        Connect your 3D printers to monitor prints in real-time, control temperatures,
        and receive AI-powered failure detection alerts.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 w-full max-w-2xl">
        {Object.entries(PROTOCOL_INFO).map(([key, info]) => (
          <div
            key={key}
            className="p-4 rounded-lg bg-surface-800 border border-surface-700"
          >
            <h4 className="font-medium text-white mb-1">{info.name}</h4>
            <p className="text-xs text-surface-400">{info.description}</p>
          </div>
        ))}
      </div>

      <button
        onClick={onAddPrinter}
        className="flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium transition-colors"
      >
        <Plus className="w-5 h-5" />
        Add Your First Printer
      </button>
    </div>
  )
}

function EventLog() {
  const eventLog = usePrinterConnectionStore((s) => s.eventLog)
  const [isExpanded, setIsExpanded] = useState(false)

  if (eventLog.length === 0) return null

  const displayEvents = isExpanded ? eventLog : eventLog.slice(0, 5)

  const getEventColor = (type: string) => {
    switch (type) {
      case 'connected':
        return 'text-green-400'
      case 'disconnected':
        return 'text-red-400'
      case 'print_started':
      case 'print_completed':
        return 'text-blue-400'
      case 'print_failed':
      case 'error':
        return 'text-red-400'
      case 'print_paused':
        return 'text-yellow-400'
      default:
        return 'text-surface-400'
    }
  }

  return (
    <div className="rounded-xl bg-surface-900 border border-surface-700 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-700">
        <h3 className="font-medium text-white">Event Log</h3>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-sm text-primary-400 hover:text-primary-300"
        >
          {isExpanded ? 'Show Less' : `Show All (${eventLog.length})`}
        </button>
      </div>
      <div className="divide-y divide-surface-800">
        {displayEvents.map((event, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-2">
            <span className={`text-xs font-medium ${getEventColor(event.type)}`}>
              {event.type.replace(/_/g, ' ').toUpperCase()}
            </span>
            <span className="text-sm text-surface-300">{event.printerId}</span>
            <span className="text-xs text-surface-500 ml-auto">
              {new Date(event.timestamp).toLocaleTimeString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
